import Delete from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { SuggestedChip } from "components/ArtificialIntelligence/SuggestedChip";
import { TextInput } from "components/TextInput";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { CompletionTiming, TaskStatus } from "generated-graphql/graphql";
import { useCallback, useState } from "react";
import { Control, Controller } from "react-hook-form";

import { StatusPicker } from "./StatusPicker";
import { TaskFormType } from "./types";

type TaskTitleHeaderProps = {
  control: Control<TaskFormType>;
  onDeleteTask?: () => void;
  initialValues: TaskFormType;
  completedOn: Date | null;
  completionTiming: CompletionTiming | null;
  completionDeltaDays: number | null;
  taskStatus: TaskStatus | null;
  isSuggested: boolean;
  /** Virtual event id used for occurrence-level comments */
  virtualEventId?: string;

  // UI configuration
  /** If true, the status picker will be read-only disabled */
  readonlyStatusPicker?: boolean;
  /** Show view in tasks button */
  showViewInTasks?: boolean;
  /** Auto-focus the title input on open */
  autoFocusTitle?: boolean;
};

export const TaskTitleHeader = ({
  control,
  initialValues,
  virtualEventId,
  completedOn,
  completionTiming,
  completionDeltaDays,
  taskStatus,
  isSuggested,
  onDeleteTask,
  showViewInTasks,
  readonlyStatusPicker = false,
  autoFocusTitle = false,
}: TaskTitleHeaderProps) => {
  const theme = useTheme();

  const [statusPopoverAnchorEl, setStatusPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [morePopoverAnchorEl, setMorePopoverAnchorEl] = useState<HTMLElement | null>(null);

  const handleDeleteTask = useCallback(() => {
    onDeleteTask?.();
    setMorePopoverAnchorEl(null);
  }, [onDeleteTask, setMorePopoverAnchorEl]);

  return (
    <Stack alignItems="center" direction="row" width="100%" paddingRight={0.5} gap={1}>
      <Controller
        name="title"
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <Stack gap={1} direction="row" flex={1}>
              {!!error && (
                <Typography variant="caption" color="error">
                  *
                </Typography>
              )}
              <TextInput
                autoFocus={autoFocusTitle}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                variant="title"
                placeholder="Task title"
              />
            </Stack>
          );
        }}
      />
      <Stack direction="row" spacing={1} alignItems="center">
        {showViewInTasks && (
          <Button
            variant="text"
            size="small"
            endIcon={<OpenInNewIcon />}
            disabled={!(initialValues.tenantId && (virtualEventId || initialValues.deadline))}
            sx={{ color: theme.palette.text.secondary, px: theme.spacing(1.5) }}
            onClick={() => {
              if (virtualEventId) {
                window.open(`/o/${initialValues.tenantId}/tasks/${virtualEventId}`, "_blank");
              } else if (initialValues.deadline) {
                window.open(
                  `/o/${initialValues.tenantId}/tasks/${initialValues.taskId + "_" + initialValues.deadline}`,
                  "_blank"
                );
              }
            }}
          >
            View in Tasks
          </Button>
        )}

        {completedOn && (
          <Typography variant="caption" noWrap sx={{ mt: 1, pr: 1, fontStyle: "italic" }}>
            Completed on {getUtcDisplayDate(completedOn, "med")}
            {taskStatus === TaskStatus.Completed &&
              formatCompletionDelta({
                completionTiming,
                completionDeltaDays,
              })}
          </Typography>
        )}

        {isSuggested ? (
          <SuggestedChip isSuggested hideAcceptButton hideRejectButton />
        ) : (
          <StatusPicker
            control={control}
            readonly={readonlyStatusPicker}
            onOpenStatusPopover={(e) => setStatusPopoverAnchorEl(e.currentTarget)}
            statusPopoverAnchorEl={statusPopoverAnchorEl}
            setStatusPopoverAnchorEl={setStatusPopoverAnchorEl}
          />
        )}
        <Tooltip title="More task actions" arrow>
          <IconButton
            onClick={(e) => setMorePopoverAnchorEl(e.currentTarget)}
            id="more-options-button"
            aria-label="More task actions"
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={morePopoverAnchorEl}
          open={Boolean(morePopoverAnchorEl)}
          onClose={() => setMorePopoverAnchorEl(null)}
          popoverTarget="more-options-button"
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          <MenuItem sx={{ width: 200 }} onClick={handleDeleteTask} disabled={!onDeleteTask}>
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            Delete task
          </MenuItem>
        </Menu>
      </Stack>
    </Stack>
  );
};

function formatCompletionDelta({
  completionTiming,
  completionDeltaDays,
}: {
  completionTiming: CompletionTiming | null;
  completionDeltaDays: number | null;
}) {
  if (completionTiming === CompletionTiming.OnTime) {
    return " (on time)";
  }

  if (completionDeltaDays == null || completionDeltaDays === 0) {
    return "";
  }

  const days = Math.abs(completionDeltaDays);
  const unit = days === 1 ? "day" : "days";

  if (completionTiming === CompletionTiming.Early) {
    return ` (${days} ${unit} early)`;
  }

  if (completionTiming === CompletionTiming.Late) {
    return ` (${days} ${unit} late)`;
  }

  return "";
}
